import './ActionStepModal.css';
import {
  Action, AdvisoryContent, ContentView, ViewElement
} from '../../../../common/global-types/AdvisoryContent';
import ProgressBar from '../ProgressBar';
import APIUtil from '../../../../common/api/APIUtil';
import useTracking from '../../../../common/tracking/useTracking';
import { getIconSizing } from '../../../../common/components/action/IconSize';

type ActionStepModalProps = {
  view: ContentView;
  setViewId: (step: number | undefined) => void;
  proxyPath: string | null;
  content: AdvisoryContent;
};

function ActionStepModal({
  view, setViewId, proxyPath, content
}: ActionStepModalProps) {
  const viewElements = view.viewElements as ViewElement;

  const trackVisibilityElement = useTracking(content, view.contentViewId);

  function handleOnClick(action: Action) {
    if (action.loadView) setViewId(action.loadView!);
    if (action.callbackPath) APIUtil.handleCallbackUrl(proxyPath, action.callbackPath);
  }

  const progressBarVisual = viewElements.visuals?.find((visual) => visual.type === 'progress-bar');

  if (!viewElements.actions) return null;

  return (
    <div id="action-step" key="action-step">
      <div className="head">
        <div className="image-container">
          <img
            src={viewElements.media?.[0].url}
            alt={viewElements.media?.[0].alt}
          />
        </div>
      </div>
      <div className="main">
        {viewElements.actions?.[1] && (
          <div
            className="if typography paragraph medium previous-step-link"
            role="button"
            tabIndex={0}
            onClick={() => handleOnClick(viewElements.actions![1])}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOnClick(viewElements.actions![1]);
              }
            }}
          >
            {viewElements.actions[1].idsIconName && viewElements.actions[1].idsIconGroup && (
              <ids-icon
                {...{ [viewElements.actions[1].idsIconName]: true, [`${viewElements.actions[1].idsIconGroup}-16`]: true }}
              />
            )}
            {viewElements.actions[1].text}
          </div>
        )}
        {progressBarVisual && progressBarVisual.settings && (
          <ProgressBar
            progressStep={progressBarVisual.settings}
          />
        )}
        <span className="if typography paragraph medium">{viewElements.paragraphs[0]}</span>
        <h2 className="if typography heading large" ref={trackVisibilityElement}>{viewElements.headlines?.[0]}</h2>
        {viewElements.paragraphs && viewElements.paragraphs.slice(1).map((paragraph) => (
          <p key={paragraph} className="if typography paragraph medium">{paragraph}</p>
        ))}
        <div className="actions">
          {viewElements.actions.length > 2 && viewElements.actions.slice(2).map((action) => {
            if (action.loadView && action.idsClassName?.includes('button')) {
              return (
                <button
                  key={action.text}
                  type="button"
                  className={`${action.idsClassName || undefined} ${viewElements.actions!.length === 3
                    ? 'action-single' : 'action-multi'}`}
                  onClick={() => handleOnClick(action)}
                >
                  {action.text}
                  {action.idsIconName && action.idsIconGroup && (
                    <ids-icon {...{ [action.idsIconName]: true, [`${action.idsIconGroup}-16`]: true }} />
                  )}
                </button>
              );
            } if (action.loadView && !action.idsClassName?.includes('button')) {
              return (
                <div
                  key={action.text}
                  className="if card elevated card-content narrow action-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOnClick(action)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOnClick(action);
                    }
                  }}
                >
                  <span className="if typography paragraph ui weight-medium action-text">
                    {action.text}
                  </span>
                  {action.idsIconName && action.idsIconGroup && (
                  <span className="action-icon">
                    <ids-icon {...{ [action.idsIconName]: true, [`${action.idsIconGroup}-${getIconSizing(action.idsClassName)}`]: true }} />
                  </span>
                  )}
                </div>
              );
            } if (action.url) {
              return (
                <a
                  key={action.text}
                  className={`${action.idsClassName || undefined} ${viewElements.actions!.length === 3
                    ? 'action-single' : 'action-multi'}`}
                  href={action.url!}
                  target={action.target ?? '_self'}
                  onClick={() => handleOnClick(action)}
                >
                  {action.text}
                  {action.idsIconName && action.idsIconGroup && (
                    <ids-icon {...{ [action.idsIconName]: true, [`${action.idsIconGroup}-${getIconSizing(action.idsClassName)}`]: true }} />
                  )}
                </a>
              );
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
}

export default ActionStepModal;
